<script setup>
import { showModal } from '@/Utils/modalBus'
import { ref, computed, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification";
import MediaflowService from '@/services/MediaflowService';

import MediaflowTypePicker from '@/components/Mediaflow/MediaflowTypePicker.vue'
import { normalizeWebPageUrl } from '@/components/Mediaflow/mediaflowMeta'
import AddPlayoutErrorAlert from '@/components/TVPlayout/AddPlayoutErrorAlert.vue'
import { useStore } from 'vuex'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import StreamSourceDropdown from "@/components/ui/StreamSourceDropdown.vue"
import FormUpload from '../Atoms/FormUpload.vue'
import AlertModal from "@/components/modals/AlertModal.vue";
import Alert from '@/components/Atoms/Alert.vue';
import ModalBox from './ModalBox.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue';

const store = useStore()
const { notify } = useNotification()

const isVisible = ref(false)
const error = ref(null)

const props = defineProps({
  mediaflowId: {
    type: String,
    required: true
  },
  flowId: {
    type: String,
    required: true
  },
  media: {
    type: Object,
    required: true
  }, 
  streamAlive: {
    type: Boolean,
    default: false
  },
  /** The scene is drafted (on air, Edit on air off): hand the settings back instead of saving. */
  stage: {
    type: Boolean,
    default: false
  },
  /** The production's output livestream: it can't also be a source. */
  destinationId: {
    type: String,
    default: ''
  }
})
const DESTINATION_SOURCE_HINT = "This livestream is this production's output, so it can't be a source."

const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const vodStreams = computed(() => store.getters['Streams/vodStreams'])
const updateMediaValidation = computed(() => {
  if (mediaData.value.name.length === 0) {
    return true
  }

  if (selectedMediaType.value.value === 'live' || selectedMediaType.value.value === 'vod') {
    return !selectedStream.value
  }

  if (selectedMediaType.value.value === 'image') {
    return !embedWatermarkTempImageId.value || !embedWatermarkTempUrl.value
  }

  if (selectedMediaType.value.value === 'url') {
    return !sourceUrlCheck.value.url
  }

  return false
})
const imageUrl = computed(() => {
  if (embedWatermarkTemp.value) {
    return embedWatermarkTemp.value
  }

  return null
})

// const liveStream = computed(() => {
// 	if (!props.media.stream) return {};
//   return allInOneStreams.value.find(stream => stream._id === props.media.stream);
// })

const mediaData = ref({
  name: '',
  type: '',
})

const updateProcessing = ref(false)
const streamSourceDropdownVisible = ref(false)
const streamSourceDropdownRef = ref(null)

const selectedStream = ref(null)
const selectedMediaType = ref({
  label: 'Livestream',
  value: 'live'
})

const mediaTypes = [
  { label: 'Livestream', value: 'live' },
  { label: 'VOD', value: 'vod' },
  { label: 'Image', value: 'image' },
  { label: 'URL', value: 'url' },
]

const maxPosterUploadMB = ref(2)
const embedWatermarkTemp = ref(null)
const embedWatermarkTempImageId = ref(null)
const embedWatermarkTempUrl = ref(null)

const features = ref({
  embedWatermark: {
    error: false,
    enabled: false,
    value: null,
    valueType: 'string'
  }
})

const featureProcessing = ref({
  embedWatermark: false,
})

const liveRestartPlayback = ref(false)
const loopVOD = ref(false)
const restartPlaybackVOD = ref(false)
const showEmptyOnEndVOD = ref(false)

const sourceUrl = ref(null)
const sourceUrlCheck = computed(() => normalizeWebPageUrl(sourceUrl.value))

const emit = defineEmits(['media-updated', 'media-staged'])

const onEmbedPosterPreview = (file, name) => {
  const selector = '#embed-watermark-input';
  const imageInput = document.querySelector(selector);
  const imageFile = imageInput.files[0];

  if (!imageFile) return;

  if (imageFile.type.indexOf('image/') === -1) {
    showModal('embed-upload-format');
    imageInput.value = null;
    return;
  }

  let maxSize = 2
  maxPosterUploadMB.value = maxSize;

  maxSize *= 1024 * 1024;

  if (imageFile.size > maxSize) {
    showModal('embed-image-size-exceed-media-edit');
    imageInput.value = null;
    return;
  }

  const validImageTypes = ['png', 'jpeg', 'gif', 'avif']
  const uploadedImageFileType = imageFile.type.replace('image/', '');
  if (
    validImageTypes &&
    validImageTypes.indexOf(uploadedImageFileType) === -1
  ) {
    showModal('embed-image-mime-type-media-edit');
    imageInput.value = null;
    return;
  }

  imageReader(imageFile, base64 => {
    embedWatermarkTemp.value = base64;
  })
}

const saveEmbedPoster = async (type) => {
  const featureProp = 'embedWatermark'
  features.value[featureProp].error = false;
  const selector = '#embed-watermark-input'
  const imageInput = document.querySelector(selector);

  const imageFile = imageInput.files[0];
  if (!imageInput.value || !imageFile) {
    features.value[featureProp].error = 'please pick an image file';
    return;
  }

  featureProcessing.value[featureProp] = true;
  const fdata = new FormData();
  fdata.append('file', imageFile);
  const res = await MediaflowService.uploadFlowImage(
    props.mediaflowId,
    props.flowId,
    fdata
  );
  if (res.success) {
    embedWatermarkTempImageId.value = res.imageId
    embedWatermarkTempUrl.value = res.imageUrl

    notify({
      group: 'success',
      text: `image uploaded successfully.`
    });
    imageInput.value = ''
  } else {
    features.value[featureProp].error = 'Unable to upload the image. Please try again later.';
  }
  featureProcessing.value[featureProp] = false;
}

const cancelUpload = async (type) => {
  const selector = '#embed-watermark-input'
  const previewFlag = embedWatermarkTemp.value 
  if (previewFlag) {
    embedWatermarkTemp.value = null;
    document.querySelector(selector).value = null;
  }
  removeImage(true);
}

const removeImage = async (toast = false) => {
  if (!embedWatermarkTempImageId.value) {
    return
  }
  // Staged: the source on air still uses this image until the draft is applied.
  if (props.stage && embedWatermarkTempImageId.value === props.media?.imageId) {
    embedWatermarkTempImageId.value = null
    embedWatermarkTempUrl.value = null
    return
  }

  try {
    await MediaflowService.deleteFlowImage(
      props.mediaflowId,
      props.flowId,
      embedWatermarkTempImageId.value
    )
    embedWatermarkTempImageId.value = null
    embedWatermarkTempUrl.value = null

    if (toast) {
      notify({ group: 'success', text: 'Image media removed' })
    }
    const settingsProp = 'embedWatermark'
    features.value[settingsProp].value = null;
  } catch (e) {
    console.log(e);
  }
}

function imageReader(file, cb) {
  if (!file) {
    return;
  }
  var reader = new FileReader();
  reader.onload = function () {
    if (cb) {
      cb(reader.result);
    }
  };
  reader.readAsDataURL(file);
}

const closeModal = (() => {
  isVisible.value = false
})

// Kept for API compatibility with the parent (`editMediaflowModalRef.toggleSidebar()`).
const toggleSidebar = () => {
  isVisible.value = !isVisible.value
  if (isVisible.value && props.media) {
    onSidebarOpened()
  }
}

const onReset = (() => {
  mediaData.value = {
    name: '',
    type: '',
  }
  error.value = null
  selectedStream.value = null

  streamSourceDropdownRef.value?.closeDropdown()
  streamSourceDropdownVisible.value = false
})

const initializeMediaData = () => {
  console.log('initializeMediaData', props.media)
  if (props.media) {
    selectedMediaType.value = mediaTypes.find(item => item.value === props.media.type);

    mediaData.value = {
      name: props.media.name || '',
      type: props.media.type || '',
    }

    if (props.media.type === 'live') {
      selectedStream.value = allInOneStreams.value.find(stream => stream._id === props.media.stream);
      
      liveRestartPlayback.value = props.media.liveRestartPlayback ?? false
    }

    if (props.media.type === 'vod') {
      selectedStream.value = { name: props.media?.fileName ?? props.media.file, source: 'dynamic', bytes: 1, type: 'vod' } // source & bytes for icon

      loopVOD.value = props.media.loop ?? false
      restartPlaybackVOD.value = props.media.restartPlayback ?? false
      showEmptyOnEndVOD.value = props.media.showEmptyOnEnd ?? false
    }

    if (props.media.type === 'image') {
      embedWatermarkTempImageId.value = props.media.imageId
      embedWatermarkTempUrl.value = props.media.imageUrl
      embedWatermarkTemp.value = props.media.imageUrl
    }

    if (props.media.type === 'url') {
      sourceUrl.value = props.media.sourceUrl
    }
  }
}

const updateMedia = async () => {
  updateProcessing.value = true
  try {
    const updateData = {
      name: mediaData.value.name,
      type: selectedMediaType.value.value,
    }

    if (updateData.type === 'live') {
      if (props.destinationId && selectedStream.value?._id === props.destinationId) {
        error.value = DESTINATION_SOURCE_HINT
        updateProcessing.value = false
        return
      }
      updateData.stream = selectedStream.value._id
      updateData.liveRestartPlayback = liveRestartPlayback.value
    }

    if (updateData.type === 'vod') {
      // vod not changed
      if (selectedStream.value.source === 'dynamic') {
        console.log('vod not changed')
        selectedStream.value = props.media
      }

      updateData.stream = selectedStream.value.file?._id
      updateData.key = selectedStream.value.folder?.key
      updateData.file = selectedStream.value.file?.id
      updateData.fileName = selectedStream.value.file?.fileName
      updateData.embedUrl = selectedStream.value.file?.embedUrl

      updateData.loop = loopVOD.value
      updateData.restartPlayback = restartPlaybackVOD.value
      updateData.showEmptyOnEnd = showEmptyOnEndVOD.value
    }

    if (updateData.type === 'image') {
      updateData.imageId = embedWatermarkTempImageId.value
      updateData.imageUrl = embedWatermarkTempUrl.value
    }

    if (updateData.type === 'url') {
      updateData.sourceUrl = sourceUrlCheck.value.url
    }

    if (props.stage) {
      emit('media-staged', props.media.id, updateData)
      updateProcessing.value = false
      closeModal()
      return
    }

    const updatedMedias = await MediaflowService.updateFlowMedia(
      props.mediaflowId,
      props.flowId,
      props.media.id,
      updateData
    );

    updateProcessing.value = false

    if (Array.isArray(updatedMedias) && updatedMedias.length > 0) {
      const media = updatedMedias.find(item => item.id === props.media.id);
      emit('media-updated', media)
      console.log('updatedMedia', media)
    }

    notify({ group: 'success', text: 'Source updated' })
    closeModal()
  } catch (err) {
    updateProcessing.value = false
    console.log(err);
    error.value = err.message
  }
}

const onSourceDropdownShow = () => {
  streamSourceDropdownVisible.value = true
}

const onSourceDropdownHide = () => {
  streamSourceDropdownVisible.value = false
}

const onSourceStreamSelected = async (stream) => {
  if (selectedMediaType.value.value === 'live' && props.destinationId && stream?._id === props.destinationId) {
    error.value = DESTINATION_SOURCE_HINT
    return
  }
  error.value = null
  selectedStream.value = stream
}

const onSidebarClosed = () => {
  onReset()
}

const onSidebarOpened = () => {
  initializeMediaData()
}
watch(isVisible, (open) => { if (!open) onSidebarClosed() })

const onMediaTypecSelect = (typ) => {
  selectedMediaType.value = typ
  selectedStream.value = null
}

watch(() => props.media, () => {
  if (props.media) {
    initializeMediaData()
  }
}, { immediate: true })

defineExpose({ toggleSidebar })

</script>

<template>
  <!-- The Livestream / Video pickers drop down inside the body: it must not clip them on desktop. -->
  <ModalBox v-model="isVisible" title="Edit source" size="sm" placement="center" mobile-placement="bottom" header-class="!px-5 !pt-5 !pb-3" body-classes="lg:!overflow-visible">
      <div class="px-5">
        <p v-if="streamAlive" class="m-0 mb-3 text-xs text-surface-8">Name and type are locked while live.</p>
        <AddPlayoutErrorAlert :error="error" v-if="error" />
          <MediaflowTypePicker class="mb-3" :options="mediaTypes" :model-value="selectedMediaType.value" label="Source type" :disabled="streamAlive" @select="onMediaTypecSelect" />

          <div class="mb-3">
            <FormInput :disabled="streamAlive" id="mediaName" label="Name" v-model="mediaData.name" placeholder="Source name" />
          </div>

          <div v-if="selectedMediaType.value === 'live'" class="mb-3">
            <label class="block text-tiny font-medium text-surface-text-1 mb-2">Livestream</label>
            <stream-source-dropdown
              ref="streamSourceDropdownRef"
              @on-show="onSourceDropdownShow"
              @on-hide="onSourceDropdownHide"
              @on-source-selected="onSourceStreamSelected"
              :dropRight="false"
              :onlyLiveSelector="true"
              :dropdownDisabled="false"
              source="multiview"
              :refLink="0"
              :selectedStream="selectedStream"
              :disabled-streams="destinationId || null"
              :disabled-tooltip="DESTINATION_SOURCE_HINT"
              :max-list-height="300"
            />
          </div>

          <div v-if="selectedMediaType.value === 'live'" class="mb-3 rounded-lg bg-surface-3 p-3">
            <FormSwitch size="md" label="Restart playback when this scene goes on air" labelPosition="right" v-model="liveRestartPlayback" />
          </div>

          <div v-if="selectedMediaType.value === 'vod'" class="mb-3">
            <label class="block text-tiny font-medium text-surface-text-1 mb-2">Video from your library</label>
            <stream-source-dropdown
              ref="streamSourceDropdownRef"
              @on-show="onSourceDropdownShow"
              @on-hide="onSourceDropdownHide"
              @on-source-selected="onSourceStreamSelected"
              :dropRight="false"
              source="mediaflow"
              :showPlaylist="false"
              :hideLive="true"
              :refLink="0"
              :selectedStream="selectedStream"
              :max-list-height="300"
            />
          </div>

          <div v-if="selectedMediaType.value === 'vod'" class="mb-3 flex flex-col gap-3 rounded-lg bg-surface-3 p-3">
            <span class="text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">Playback</span>
            <FormSwitch size="md" label="Loop" labelPosition="right" v-model="loopVOD" />
            <FormSwitch size="md" label="Restart from the beginning when this scene goes on air" labelPosition="right" v-model="restartPlaybackVOD" />
            <FormSwitch size="md" label="Show nothing when playback ends" labelPosition="right" v-model="showEmptyOnEndVOD" />
          </div>

          <div v-if="selectedMediaType.value === 'image'" class="mb-3">
            <label class="block text-tiny font-medium text-surface-text-1 mb-2">Image (PNG, JPG, GIF or AVIF, up to {{ maxPosterUploadMB }} MB)</label>
            <div class="flex justify-between items-center gap-x-2">
              <div class="flex">
                <img v-if="imageUrl" width="100px" :src="imageUrl" class="w-full size-full md:w-[100px]"  />
                <p v-else class="text-tiny text-surface-6">No file chosen</p>
              </div>

              <div class="flex">
                <FormUpload :class="{hidden: embedWatermarkTemp }" name="watermark" id="embed-watermark-input" placeholder="No file chosen" type="secondary"
                  :label="embedWatermarkTemp ? 'Change' : 'Select image'" :on-file-change="onEmbedPosterPreview" />
                <FormButton v-if="embedWatermarkTemp"  :class="{hidden: embedWatermarkTempImageId}" :disabled="featureProcessing.embedWatermark"
                  @click="saveEmbedPoster('watermark')">
                  {{ featureProcessing.embedWatermark ? 'Saving' : 'Save' }}
                </FormButton>
                <FormButton v-if="embedWatermarkTemp || features.embedWatermark.value"
                  class="ml-2"
                  :disabled="featureProcessing.embedWatermark" type="secondary" @click="cancelUpload('watermark')">
                  <span v-if="embedWatermarkTemp">Cancel</span>
                  <span v-else>Remove</span>
                </FormButton>
              </div>
            </div>
            <Alert v-show="features.embedWatermark.error" type="error" class="mt-2">
              {{ features.embedWatermark.error }}
            </Alert>
          </div>

          <div v-if="selectedMediaType.value === 'url'" class="mb-3">
            <FormInput id="edit-source-url" v-model="sourceUrl" label="Web page URL" placeholder="https://…" :is-error="!!sourceUrlCheck.error" :helper="sourceUrlCheck.error" />
          </div>
      </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 px-5 pb-5 pt-2 sm:flex-row sm:justify-end">
        <FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" @click="closeModal">Cancel</FormButton>
        <FormButton type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="updateMediaValidation || updateProcessing" :is-loading="updateProcessing" data-testid="edit-source-submit" @click="updateMedia">Save changes</FormButton>
      </div>
    </template>
  </ModalBox>

  <alert-modal :message="'Uploaded image too large. Image must not exceed ' + maxPosterUploadMB + 'MB'"
    modal-id="embed-image-size-exceed-media-edit" />
  <alert-modal modal-id="embed-image-mime-type-media-edit" message="Only PNG/JPG/GIF/AVIF image files are allowed" />
</template>